import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axiosInstance';

const initialState = {
  settings: null,
  publicSettings: null,
  loading: false,
  saving: false,
  error: null,
  success: false,
};

// Async Thunk: Fetch Settings for Admin (with Secrets)
export const fetchAdminGatewaySettings = createAsyncThunk(
  'gateway/fetchAdminGatewaySettings',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/gateways/admin');
      if (res.data.success) {
        return res.data.settings;
      }
      return rejectWithValue('Failed to fetch gateway settings');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Async Thunk: Fetch Settings for Client (No Secrets)
export const fetchPublicGatewaySettings = createAsyncThunk(
  'gateway/fetchPublicGatewaySettings',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/gateways/public');
      if (res.data.success) {
        return res.data.settings;
      }
      return rejectWithValue('Failed to fetch public gateway settings');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { gateway } = getState();
      if (gateway.publicSettings) {
        return false;
      }
    }
  }
);

// Async Thunk: Update Gateway Settings (Admin Only)
export const updateGatewaySettings = createAsyncThunk(
  'gateway/updateGatewaySettings',
  async (values, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put('/gateways', values);
      if (res.data.success) {
        return res.data.settings;
      }
      return rejectWithValue(res.data.message || 'Failed to update gateway settings');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const gatewaySlice = createSlice({
  name: 'gateway',
  initialState,
  reducers: {
    clearGatewaySuccess: (state) => {
      state.success = false;
    },
    clearGatewayError: (state) => {
      state.error = null;
    },
    setPublicSettings: (state, action) => {
      state.publicSettings = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Admin Settings
      .addCase(fetchAdminGatewaySettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminGatewaySettings.fulfilled, (state, action) => {
        state.settings = action.payload;
        state.loading = false;
      })
      .addCase(fetchAdminGatewaySettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Public Settings
      .addCase(fetchPublicGatewaySettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPublicGatewaySettings.fulfilled, (state, action) => {
        state.publicSettings = action.payload;
        state.loading = false;
      })
      .addCase(fetchPublicGatewaySettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update Settings
      .addCase(updateGatewaySettings.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateGatewaySettings.fulfilled, (state, action) => {
        state.settings = action.payload;
        state.publicSettings = action.payload;
        state.saving = false;
        state.success = true;
      })
      .addCase(updateGatewaySettings.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
        state.success = false;
      });
  }
});

export const { clearGatewaySuccess, clearGatewayError, setPublicSettings } = gatewaySlice.actions;
export default gatewaySlice.reducer;
